import LayoutMain from 'layouts/Main';
import App, { AppContext } from 'next/app';
import Router from 'next/router';

import '@root/lib/bugsnag';
import type { GtUser, SeoData, WindowUser } from 'types';
import AjaxService, { AjaxError } from 'services/ajax';
import { waitUntilGtagHasLoaded } from 'services/google';
import auth from 'utils/auth';
import { getPathFromUrl, getSEO } from 'utils/utils';
import { getPageSchema } from 'data/PageSchema';
import { HeadProps } from 'partials/nav/Head';
import { getConsentPreferences } from 'utils/metrics';
import { startArrivalReplay } from 'utils/arrivalReplay';
import { startConsentMirror } from 'utils/consentMirror';

import '../styles/globals.css';

const ajax = AjaxService();

const identify = () => {
  if (window.gt.user && window.analytics) {
    window.analytics.identify(
      window.gt.user.id,
      {
        firstName: window.gt.user.firstName,
        lastName: window.gt.user.lastName,
        email: window.gt.user.email,
        phone: window.gt.user.phone,
        smsOptIn: window.gt.user.smsOptIn,
        primaryEventId: window.gt.user.primaryEventId || '',
        brand: 'menguin',
        emailOptIn: window.gt.user.emailOptIn,
        state: window.gt.user.state,
      },
      {
        All: true,
        Iterable: {
          phoneNumber: window.gt.user.phone,
        },
        /**
         * @todo When a user signs up we intend to manually opt them into cookies,
         * since we require things like Iterable for user communications. We need
         * to check what happens when someone goes through sign up, and then changes
         * their preferences afterward.
         */
        context: getConsentPreferences(),
      } as SegmentAnalytics.SegmentOpts,
    );
  }
};

const fetchSession = async () => {
  try {
    return await ajax.post<Promise<GtUser>>('/api/user/session', {
      credentials: 'same-origin',
    });
  } catch (err: unknown) {
    const { status } = err as AjaxError;

    if (status === 401) {
      return null;
    } else {
      throw err;
    }
  }
};

const configureAnalyticsReporting = async () => {
  try {
    await waitUntilGtagHasLoaded();

    gtag('config', process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID ?? '');
  } catch (e) {
    console.error(e);
  }
};
type Props = HeadProps & {
  canonicalUrl: string | null;
  pageProps: any;
  pathname: string;
  seo: SeoData;
  err?: Error;
};

export default class extends App<Props> {
  schema: DynamicObject | null = this.props.pageProps.schema ?? this.props.schema;

  /**
   * Reruns on every /page component load
   */
  static async getInitialProps({ Component, ctx }: AppContext) {
    const pathname = getPathFromUrl(ctx.asPath ?? '/');
    const canonicalUrl = ctx.asPath?.includes('/app') ? null : `https://www.menguin.com${(ctx.asPath || '').split('?')[0]}`;
    const schema = getPageSchema(pathname);

    const props = {
      pathname: ctx.asPath,
      pageProps: {},
      // Pull SEO data from compiled SEO traits file (utils/seo)
      // or cms in Redis cache
      seo: await getSEO(getPathFromUrl(ctx.asPath!)),
      canonicalUrl,
      err: ctx.err,
      schema,
    };

    if (Component.getInitialProps) {
      const pageProps = {
        ...(await Component.getInitialProps(ctx)),
      };
      return {
        ...props,
        pageProps,
      };
    }

    return {
      ...props,
    };
  }

  /**
   * All actions within componentDidMount happen client side only.
   * _app.tsx.ComponentDidMount will only mount once throughout the
   * lifecycle of the application. Think of this as a "bootstrap"
   * for initial js load within the entire application.
   */
  async componentDidMount() {
    startConsentMirror();
    startArrivalReplay();

    const user = await fetchSession();

    user && auth.storeUser(user);
    window.organization = 'menguin';

    if (!user) {
      window.localStorage.removeItem('jwt');
    }

    identify();

    /**
     * This will create a callback that runs everytime
     * a new page is loaded via client side routing.
     * i.e. using <Link>
     */
    Router.events.on('routeChangeComplete', () => {
      window.analytics.page({}, { context: getConsentPreferences() });

      identify();

      window.gt = {
        ...window.gt,
        user: user as WindowUser,
      };

      if (!user) {
        window.localStorage.removeItem('jwt');
      }
    });

    configureAnalyticsReporting();
  }

  render() {
    const { props } = this;
    const { Component, pageProps, err } = this.props;

    const seo: SeoData = pageProps.seo ?? props.seo ?? {};
    const canonicalUrl: string = pageProps.canonicalUrl ?? props.canonicalUrl ?? '';
    const schema: DynamicObject | null = pageProps.schema ?? props.schema;

    const propsWithSeoData = {
      ...props,
      seo,
      canonicalUrl,
      schema,
    };

    return (
      <LayoutMain {...propsWithSeoData}>
        <Component {...pageProps} err={err} />
      </LayoutMain>
    );
  }
}
